// src/utils/authUtils.js

const decodeJWT = (token) => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error("Error decoding JWT token:", error);
    return null;
  }
};

export const getUserInfoFromToken = () => {
  const token = getAuthToken();
  if (!token) return null;

  try {
    const decoded = decodeJWT(token);
    if (!decoded) return null;

    return {
      userId: decoded.sub,
      email: decoded.email,
      emailVerified: decoded.email_verified,
      username: decoded["cognito:username"] || decoded.username,
      groups: decoded["cognito:groups"] || [],
      authTime: decoded.auth_time ? new Date(decoded.auth_time * 1000) : null,
      tokenIssuedAt: decoded.iat ? new Date(decoded.iat * 1000) : null,
      tokenExpiresAt: decoded.exp ? new Date(decoded.exp * 1000) : null,
    };
  } catch (error) {
    console.error("Error extracting user info from token:", error);
    return null;
  }
};

// STARTUP key kept for brand kit compatibility — displays as "Artist Name" in the UI
export const getArtistName = () => localStorage.getItem("STARTUP");

/**
 * Returns the site owner's (artist's) tenantId from the env var.
 * Used by public pages (gallery, bio) so unauthenticated visitors always
 * see Teri's content regardless of auth state.
 */
export const getOwnerTenantId = () => process.env.REACT_APP_OWNER_TENANT_ID || null;

/**
 * tenantId = raw Cognito sub UUID from JWT.
 * No startup-name prefix — matches x20-creative pattern.
 */
export const getTenantId = () => {
  const userInfo = getUserInfoFromToken();
  if (userInfo?.userId) return userInfo.userId;
  return localStorage.getItem("TENANT_ID") || null;
};

export const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const decoded = decodeJWT(token);
    if (!decoded || !decoded.exp) return true;
    const buffer = 5 * 60 * 1000; // 5-minute buffer
    return Date.now() >= decoded.exp * 1000 - buffer;
  } catch {
    return true;
  }
};

export const getTokenExpirationTime = (token) => {
  if (!token) return null;
  try {
    const decoded = decodeJWT(token);
    return decoded?.exp ? decoded.exp * 1000 : null;
  } catch {
    return null;
  }
};

const closeAllModals = () => {
  window.dispatchEvent(new CustomEvent("forceCloseAllModals", { detail: { reason: "logout" } }));
  document
    .querySelectorAll('[class*="modal"], [class*="Modal"], [class*="dialog"], [class*="Dialog"], [role="dialog"]')
    .forEach((el) => { if (el.style) el.style.display = "none"; });
};

export const clearAuthState = () => {
  closeAllModals();
  localStorage.removeItem("jwtToken");
  localStorage.removeItem("accessToken");
  localStorage.removeItem("cognitoUser");
  localStorage.removeItem("lastActivityTime");
  Object.keys(localStorage).forEach((key) => {
    if (key.includes("cognito") || key.includes("amplify") || key.includes("aws")) {
      localStorage.removeItem(key);
    }
  });
};

export const isAuthenticated = () => {
  const token = localStorage.getItem("jwtToken");
  if (!token) return false;
  if (isTokenExpired(token)) {
    clearAuthState();
    return false;
  }
  return true;
};

export const getAuthToken = () => {
  const token = localStorage.getItem("jwtToken");
  if (!token) return null;
  if (isTokenExpired(token)) {
    clearAuthState();
    return null;
  }
  return token;
};

export const setAuthToken = (token) => {
  localStorage.setItem("jwtToken", token);
  localStorage.setItem("lastActivityTime", Date.now().toString());
};
